import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Grid,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import WhatsAppMessageModal from './WhatsAppMessageModal'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import MedAgDataGrid from '../../components/MedAgDataGrid'
import SelectableSearch from '../../components/SelectableSearch'
import styles from './listOfCamps.module.scss'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import {
  GetClinicalDataListApi,
  SendReportWhatsappMessageApi,
  UploadPftScreeningApi,
} from '../../services/CampManagementService'
import { toast } from 'react-toastify'
import { getBucketFile } from '../../services/constants'
import { makeStyles } from '../../utils/hooks/makeStyles'
import { NORMAL } from './constants/Constants'

const TOOLTIP_COMPONENTS_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: '#f5f5f5',
      color: '#333',
      border: '1px solid #bbb',
      padding: '12px',
      maxWidth: '400px',
    },
  },
} as const

function PFTList() {
  const classes = useStyles()
  const location = useLocation()
  const { camp, type } = location.state || {}
  const [openModal, setOpenModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [normality, setNormality] = useState('')
  const [description, setDescription] = useState('')
  const [predictedFev1, setPredictedFev1] = useState('')
  const [fev1Fvc, setFev1Fvc] = useState('')
  const [touchedFev1, setTouchedFev1] = useState(false)
  const [touchedFev1Fvc, setTouchedFev1Fvc] = useState(false)
  const [data, setData] = useState<any[]>([])

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)

  // Date and Search states
  const [startDate, setStartDate] = useState<Dayjs | null>(
    camp?.fromDate ? dayjs(camp.fromDate) : null
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(
    camp?.toDate ? dayjs(camp.toDate) : null
  )
  const [searchParam, setSearchParam] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [pdfFileUrl, setPdfFileUrl] = useState<string>('')
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [activeTab, setActiveTab] = useState<'open' | 'closed'>('open')

  // WhatsApp modal state
  const [openWhatsAppModal, setOpenWhatsAppModal] = useState(false)
  const [whatsAppRecord, setWhatsAppRecord] = useState<any>(null)

  // Status is displayed as a plain field now (no click action)

  // Handle search from SelectableSearch component
  const handleSearch = useCallback((param: string, query: string) => {
    setSearchParam(param)
    setSearchValue(query)
    setPage(0) // Reset to first page on new search
  }, [])

  // Fetch clinical data
  const fetchClinicalData = useCallback(async () => {
    // Validate date range before making API call
    if (startDate && endDate && startDate.isAfter(endDate)) {
      toast.error('Start date must be before or equal to end date')
      return
    }

    try {
      setLoading(true)
      const body = {
        campId: camp?.campId,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : '',
        endDate: endDate ? endDate.format('YYYY-MM-DD') : '',
        patientUhid: searchParam === 'patientUhid' ? searchValue : '',
        phoneNumber: searchParam === 'mobile' ? searchValue : '',
        employeeId: searchParam === 'employeeId' ? searchValue : '',
        type: type || 'pft',
        page: page,
        size: pageSize,
        status: activeTab === 'open' ? 'open' : 'closed',
      }

      const response = await GetClinicalDataListApi(body)
      if (response?.success) {
        // const closed = response.data?.records.filter((r: any) => r.status) || []
        // const opened =
        //   response.data?.records.filter((r: any) => !r.status) || []
        // setData(closed)
        // setOpenData(opened)
        setData(response?.data?.records || [])
        setTotalRows(response?.data?.totalRecords || 0)
      }
    } catch (error) {
      console.error('Error fetching clinical data:', error)
    } finally {
      setLoading(false)
    }
  }, [
    camp?.campId,
    startDate,
    endDate,
    searchParam,
    searchValue,
    type,
    page,
    pageSize,
    activeTab,
  ])

  useEffect(() => {
    fetchClinicalData()
  }, [fetchClinicalData])

  const handleUpdateClick = useCallback((record: any) => {
    setSelectedRecord(record)
    setNormality(record?.status || '')
    setDescription(record?.interpretation || '')
    setPredictedFev1(record?.predictedFev1 || '')
    setFev1Fvc(record?.fev1Fvc || '')
    setTouchedFev1(false)
    setTouchedFev1Fvc(false)
    setPdfFileUrl('')
    setOpenModal(true)
  }, [])

  // Fetch PDF file when record is selected
  useEffect(() => {
    const fetchPdfFile = async () => {
      if (selectedRecord && selectedRecord?.documentUrl) {
        setLoadingPdf(true)
        try {
          const fileData: any = await getBucketFile(
            selectedRecord.documentUrl,
            `${type || 'PFT'} File`
          )
          setPdfFileUrl(fileData?.pdfFileUrl || '')
        } catch (error) {
          console.error('Error fetching PDF file:', error)
          toast.error('Failed to load PDF file')
        } finally {
          setLoadingPdf(false)
        }
      }
    }
    fetchPdfFile()
  }, [selectedRecord, type])

  const handleClose = useCallback(() => {
    setOpenModal(false)
    setSelectedRecord(null)
    setNormality('')
    setDescription('')
    setPredictedFev1('')
    setFev1Fvc('')
    setTouchedFev1(false)
    setTouchedFev1Fvc(false)
    setPdfFileUrl('')
  }, [])

  const handleCopy = useCallback((data: any) => {
    const textToCopy = `
    Name: ${data?.patientName}
    Emp ID: ${data?.employeeId}
    Phone: ${data?.phoneNumber}
    PFT:${
      data?.status
        ? data?.status?.charAt(0)?.toUpperCase() + data?.status?.slice(1)
        : ''
    }
    muhID: ${data?.patientUhid}
    Gender: ${data?.gender}
    Age: ${data?.age}
    Sample Collected By: ${data?.counterNumber ?? ''}
    Comment: ${data?.interpretation}`

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => toast.success('Information copied to clipboard'))
      .catch(() => toast.error('Failed to copy to clipboard'))
  }, [])

  const handleSubmit = useCallback(async () => {
    // Validate inputs
    if (!normality) {
      toast.error('Please select abnormality status')
      return
    }

    if (!description?.trim()) {
      toast.error('Please enter notes/description')
      return
    }

    if (!predictedFev1) {
      toast.error('Predicted FEV1 is required')
      return
    }

    if (!fev1Fvc) {
      toast.error('FEV1/FVC is required')
      return
    }

    if (!selectedRecord) {
      toast.error('No record selected')
      return
    }

    setSubmitLoading(true)
    try {
      const payload = {
        campId: selectedRecord?.campId || camp?.id || '',
        patientUhid: selectedRecord?.patientUhid || '',
        visitId: selectedRecord?.visitId || '',
        documentUrl: selectedRecord?.documentUrl || '',
        normal: normality === NORMAL,
        status: normality,
        interpretation: description,
        predictedFev1: predictedFev1,
        fev1Fvc: fev1Fvc,
        doctorId: selectedRecord?.doctorId,
        doctorName: selectedRecord?.doctorName,
        patientName: selectedRecord?.patientName,
      }

      const response = await UploadPftScreeningApi(payload)

      if (response.success) {
        toast.success(
          response.message || `${type || 'PFT'} updated successfully`
        )
        handleCopy({
          ...selectedRecord,
          status: normality,
          interpretation: description,
        })
        await fetchClinicalData()
        handleClose()
      } else {
        toast.error(response.message || 'Failed to update record')
      }
    } catch (error: any) {
      console.error('Error updating record:', error)
      toast.error(error?.message || 'Failed to update record')
    } finally {
      setSubmitLoading(false)
    }
  }, [
    normality,
    description,
    predictedFev1,
    fev1Fvc,
    selectedRecord,
    camp?.id,
    type,
    handleCopy,
    fetchClinicalData,
    handleClose,
  ])

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'S. No.',
        field: 'sNo',
        flex: 0.5,
        valueGetter: (params: any) => {
          const index = params?.node?.rowIndex ?? 0
          return index + 1
        },
      },
      {
        headerName: 'Doctor Name',
        field: 'doctorName',
        flex: 1.5,
      },
      {
        headerName: 'Patient Name',
        field: 'patientName',
        flex: 1.5,
      },
      {
        headerName: 'Patient UHID',
        field: 'patientUhid',
        flex: 1,
      },
      {
        headerName: 'Camp Name',
        field: 'campName',
        flex: 1.2,
        cellRenderer: () => {
          return <div>{camp?.name}</div>
        },
      },

      {
        headerName: 'Doctor ID',
        field: 'doctorId',
        flex: 0.8,
      },
      {
        headerName: 'Status',
        field: 'status',
        flex: 1,
        cellRenderer: (params: any) => {
          return (
            <div className={classes.statusCell}>
              <span>
                {params?.data?.status
                  ? params.data.status.charAt(0).toUpperCase() +
                    params.data.status.slice(1)
                  : ''}
              </span>

              {params?.data?.interpretation && (
                <Tooltip
                  title={
                    <div className={classes.tooltipContent}>
                      {params?.data?.interpretation}
                    </div>
                  }
                  componentsProps={TOOLTIP_COMPONENTS_PROPS}
                >
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
              )}
              {params?.data?.status && (
                <ContentCopyIcon
                  onClick={() => handleCopy(params?.data)}
                  className={classes.infoIcon}
                />
              )}
              {params?.data?.status && (
                <WhatsAppIcon
                  onClick={() => handleWhatsAppClick(params?.data)}
                  className={classes.whatsappIcon}
                />
              )}
            </div>
          )
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        flex: 1,
        cellRenderer: (params: any) => {
          return (
            <div>
              <span
                onClick={() => {
                  params?.context?.onUpdateClick(params?.data)
                }}
                className={styles.campEditText}
              >
                View
              </span>
            </div>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleWhatsAppClick = useCallback((record: any) => {
    setWhatsAppRecord(record)
    setOpenWhatsAppModal(true)
  }, [])

  const handleWhatsAppModalClose = useCallback(() => {
    setOpenWhatsAppModal(false)
    setWhatsAppRecord(null)
  }, [])

  const handleWhatsAppSubmit = useCallback(
    async (patientInfo: any, mobileNumber: string) => {
      handleWhatsAppModalClose()

      const placeHolder =
        patientInfo?.status == NORMAL
          ? [patientInfo?.patientName, 'PFT', 'PFT']
          : [patientInfo?.patientName, 'PFT', 'PFT', 'PFT', 'PFT']

      const payload = {
        to: `+91${mobileNumber}`,
        templateName:
          patientInfo?.status == NORMAL
            ? 'normal_clinic_screening_result'
            : 'abnormal_clinic_screening_result',
        placeholders: placeHolder,
        documentUrl: patientInfo?.documentUrl ?? '',
        documentName: 'PFT Report.pdf',
      }
      try {
        const response = await SendReportWhatsappMessageApi(payload)

        if (response.success) {
          toast.success(
            response.message || `${type || 'ECG'} updated successfully`
          )
          handleCopy(patientInfo)
          await fetchClinicalData()
          handleClose()
        } else {
          toast.error(response.message || 'Failed to update record')
        }
      } catch (error: any) {
        console.error('Error sending WhatsApp message:', error)
        toast.error(error?.message || 'Failed to send WhatsApp message')
      }
    },
    [handleWhatsAppModalClose, fetchClinicalData, handleClose, handleCopy, type]
  )

  const context = useMemo(
    () => ({
      onUpdateClick: handleUpdateClick,
    }),
    [handleUpdateClick]
  )

  return (
    <Box className={classes.root}>
      {/* Title, Subtitle and Filters in the same row */}
      <Box className={classes.filtersBox}>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs>
            <Typography variant='h5' className={classes.title}>
              {type || 'PFT'} List
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Camp: {camp?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Start Date'
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: false,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='End Date'
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: false,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item>
            <Box className={classes.searchWrapper}>
              <SelectableSearch
                items={['employeeId', 'mobile', 'patientUhid']}
                onSearch={handleSearch}
                placeHolder='Search by UHID, Mobile or Employee ID'
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs + grid as one unit */}
      <Box className={classes.tableContainer}>
        <Tabs
          value={activeTab}
          onChange={(_e, val) => {
            setActiveTab(val)
            setPage(0)
          }}
          className={classes.tabBar}
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          <Tab label='Open' value='open' className={classes.tab} />
          <Tab label='Completed' value='closed' className={classes.tab} />
        </Tabs>

        <Box className={classes.gridBox}>
          <MedAgDataGrid
            className='ag-theme-alpine'
            tableHeight='100%'
            rowData={data}
            columnDefs={columnDefs}
            pagination={true}
            page={page}
            pageSizeOptions={[50, 100, 250, 500, 1000]}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalRows}
            headerHeight={40}
            rowHeight={48}
            filterActions={null}
            globalSearch={null}
            searchField={false}
            exportIcon={false}
            context={context}
            loading={loading}
          />
        </Box>
      </Box>

      <Dialog open={openModal} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>
          Review {type || 'PFT'} - {selectedRecord?.patientName}
          <IconButton
            aria-label='close'
            onClick={handleClose}
            className={classes.closeBtn}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* PDF Viewer */}
          {loadingPdf ? (
            <Box className={classes.pdfLoading}>
              <Typography>Loading PDF...</Typography>
            </Box>
          ) : pdfFileUrl ? (
            <Box className={classes.pdfBox}>
              <iframe
                src={pdfFileUrl}
                width='100%'
                height='100%'
                title='PDF Viewer'
                className={classes.iframe}
              />
            </Box>
          ) : selectedRecord?.documentUrl ? (
            <Box className={classes.pdfError}>
              <Typography color='error'>Failed to load PDF</Typography>
            </Box>
          ) : null}

          {/* Predicted FEV1 and FEV1/FVC */}
          <Box className={classes.fevRow}>
            <TextField
              size='small'
              label='Predicted FEV1'
              fullWidth
              required
              value={predictedFev1}
              onChange={(e) => setPredictedFev1(e.target.value)}
              onBlur={() => setTouchedFev1(true)}
              placeholder='Enter Predicted FEV1...'
              error={touchedFev1 && !predictedFev1}
              helperText={
                touchedFev1 && !predictedFev1
                  ? 'Predicted FEV1 is required'
                  : ''
              }
            />
            <TextField
              size='small'
              label='FEV1/FVC'
              fullWidth
              required
              value={fev1Fvc}
              onChange={(e) => setFev1Fvc(e.target.value)}
              onBlur={() => setTouchedFev1Fvc(true)}
              placeholder='Enter FEV1/FVC...'
              error={touchedFev1Fvc && !fev1Fvc}
              helperText={
                touchedFev1Fvc && !fev1Fvc ? 'FEV1/FVC is required' : ''
              }
            />
          </Box>

          {/* Abnormality Status Radio Buttons */}
          <FormControl component='fieldset' className={classes.formControl}>
            <FormLabel component='legend'>Abnormality Status</FormLabel>
            <RadioGroup
              row
              value={normality}
              onChange={(e) => setNormality(e.target.value)}
            >
              <FormControlLabel
                value='normal'
                control={<Radio />}
                label='Normal'
              />
              <FormControlLabel
                value='abnormal'
                control={<Radio />}
                label='Abnormal'
              />
            </RadioGroup>
          </FormControl>

          {/* Notes/Description */}
          <TextField
            label='Notes'
            multiline
            rows={4}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Enter detailed description or findings...'
            variant='outlined'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color='secondary'
            disabled={submitLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            color='primary'
            disabled={submitLoading}
          >
            {submitLoading ? 'Submitting...' : 'Copy & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      <WhatsAppMessageModal
        open={openWhatsAppModal}
        onClose={handleWhatsAppModalClose}
        patient={whatsAppRecord}
        onSubmit={handleWhatsAppSubmit}
      />
    </Box>
  )
}

export default PFTList

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: theme.spacing(1.5),
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  title: {
    marginBottom: 0,
    lineHeight: 1.2,
  },
  subtitle: {
    marginBottom: 0,
  },
  filtersBox: {
    marginBottom: theme.spacing(1),
    flexShrink: 0,
  },
  searchWrapper: {
    '& .search-input': {
      height: '40px',
      boxSizing: 'border-box',
      padding: '0 8px',
      fontSize: 14,
      width: '100%',
    },
    '& .options-wrapper': {
      width: '100%',
    },
  },
  tableContainer: {
    flex: '0 0 90%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  },
  tabBar: {
    flexShrink: 0,
    background: '#f0f2f5',
    borderBottom: '1px solid #e0e0e0',
    minHeight: '44px',
    padding: '6px 8px 0',
  },
  tab: {
    minHeight: '34px',
    padding: '4px 20px',
    borderRadius: '6px 6px 0 0',
    fontWeight: 500,
    fontSize: '13px',
    textTransform: 'none',
    color: '#666',
    marginRight: '4px',
    '&.Mui-selected': {
      background: '#fff',
      color: '#1976d2',
      borderTop: '1px solid #e0e0e0',
      borderLeft: '1px solid #e0e0e0',
      borderRight: '1px solid #e0e0e0',
      borderBottom: '1px solid #fff',
      marginBottom: '-1px',
    },
    '&:hover:not(.Mui-selected)': {
      background: '#e4e8ed',
      borderRadius: '6px 6px 0 0',
    },
  },
  gridBox: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  pdfLoading: {
    marginBottom: theme.spacing(3),
    height: '500px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfBox: {
    marginBottom: theme.spacing(3),
    height: '500px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  pdfError: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  fevRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  iframe: {
    border: 'none',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tooltipContent: {
    whiteSpace: 'pre-wrap' as const,
    wordWrap: 'break-word' as const,
    maxWidth: '400px',
  },
  infoIcon: {
    fontSize: '18px',
    cursor: 'pointer',
    color: '#1976d2',
  },
  whatsappIcon: {
    fontSize: '18px',
    cursor: 'pointer',
    color: '#25D366',
  },
}))
